import React, { useEffect, useState } from 'react';
import { Api, pathJoin } from '../../../../services/api';
import { Checkbox } from '../../inputs/checkbox';
import { LoadText } from '../../load/loadText';
import { Button } from '../../inputs/button';
import { Query } from '../../view/query';
import { Grid } from '../grid';

import styles from './queryGrid.module.css';
import { Notify } from '../../../../services/notification/notify';

/**
 * @param {Object} props
 * @param {string} props.singleResourceName Single name (Example: branch)
 * @param {string} props.pluralResourceName plural name (Example: branches)
 * @param {string} props.restEndpoint Rest Endpoint of the API to query (Example: /locations/branches)
 * @param {string} props.listUrl List url for the webapp
 * @param {function(Object.<string, Object>): string} props.nameFunction The function for getting the name from the raw API data
 * @param {boolean} [props.enableSearchBar=true] Is the search bar shown
 * @param {boolean} [props.queryOnLoad=true] Should a query be performed when the component loads
 * @param {number?} [props.scope=null] Scope to be passed to the API
 * @param {number?} [props.maxResults=null] Max Results to be returned by the API
 * @param {((props: any) => import('react/jsx-runtime').JSX.Element)[]} [props.actions=[]] Actions
 * @param {string[]} [props.readPermissions=[]] Read Permissions
 * @param {string[]} [props.writePermissions=[]] Write Permissions
 * @param {string|null} [props.refreshToken=null] On change, refreshes the data
 * @param {string|null} [props.queryParamPrefix=null] If given, will save data to query parameters. Prefix to all multiple tables a single endpoint.
 * @param {boolean} [props.editMode=null] Allows inline editing
 * @param {Object.<string, {label: string, sortable?: boolean, size?: number|string, passOnClick?: boolean, formatFunction: ((value: Object.<string, Object>) => React.ReactNode|string)|null}>} props.columns Columns to render
 * @param {boolean} [props.stickyHeader=true] Should the header be sticky.
 * @param {QueryGridSelectModeEnum} [props.selectMode=typeof QueryGridSelectMode.Multi] Select mode
 * @param {((v: { [x: string]: any; }[]) => void)|null} [props.onSelect=null] Called on Select
 */
export const QueryGrid = ({
    stickyHeader = true,
    selectMode = QueryGridSelectMode.MultiIfWritable,
    editMode = false,
    columns = {},
    ...props
}) => {
    const getLoadingRow = () => 
        Object.keys(columns).reduce((a, key) => {
            a[key] = <LoadText height={'22px'} />;
            return a;
        }, { id: Math.random() });

    const loadingDataSet = () => [...new Array(10)].map(() => getLoadingRow());

    return (
        <Query {...props}>
            {({ loading, rawData, sort, setSort, canWrite, selected, setSelected, isEditing, setIsEditing, refreshData, setLoading, setDisablePaging }) => (
                <Child
                    rawData={rawData}
                    selectMode={selectMode}
                    selected={selected}
                    setSelected={setSelected}
                    editMode={editMode}
                    canWrite={canWrite}
                    columns={columns}
                    loading={loading}
                    loadingDataSet={loadingDataSet}
                    stickyHeader={stickyHeader}
                    sort={sort}
                    setSort={setSort}
                    isEditing={isEditing}
                    setIsEditing={setIsEditing}
                    restEndpoint={props.restEndpoint}
                    resourceName={props.singleResourceName}
                    refreshData={refreshData}
                    setLoading={setLoading}
                    setDisablePaging={setDisablePaging}
                />
            )}
        </Query>
    );
};

const Child = ({
    rawData,
    selectMode,
    selected,
    setSelected,
    editMode,
    canWrite,
    columns,
    loading,
    loadingDataSet,
    stickyHeader,
    sort,
    setSort,
    isEditing,
    setIsEditing,
    restEndpoint,
    resourceName,
    refreshData,
    setLoading,
    setDisablePaging,
}) => {
    const [editableData, setEditableData] = useState([]);
    const [changedIds, setChangeIds] = useState([]);
    const [deletedIds, setDeletedIds] = useState([]);
    const [addedIds, setAddedIds] = useState([]);

    const editing = () => isEditing && editMode;

    const isSelectable = () => {
        return (
            selectMode === QueryGridSelectMode.Multi ||
            selectMode === QueryGridSelectMode.Pick ||
            (selectMode === QueryGridSelectMode.MultiIfWritable && canWrite) ||
            (selectMode === QueryGridSelectMode.PickIfWritable && canWrite)
        );
    };

    useEffect(() => {
        setEditableData(rawData || []);
    }, [rawData]);

    const updateSelected = (id, event) => {
        if (isSelectable()) {
            setSelected(prevSelected =>
                event.target.checked
                    ? [...prevSelected, id]
                    : prevSelected.filter(s => s !== id)
            );
        }
    };

    const handleFieldChange = (id, key, value) => {
        setEditableData(data =>
            data.map(item => item.id === id ? { ...item, [key]: value } : item)
        );
        
        // Record changes for undo or patch operations
        setChangeIds(ids => [...(new Set([...ids, id]))]);
    };

    const handleDelete = id => {
        setDeletedIds(ids => [...ids, id]);
    };

    const save = async () => {
        setDisablePaging(true);

        // Delete items
        if (deletedIds.length > 0) {
            try {
                await Api.delete(pathJoin(restEndpoint, deletedIds.join(',')));
            } catch (e) {
                console.error(e);
                Notify.error('Error Deleting', `An error occurred while trying to delete the ${resourceName} and the operation could not be finished`);
            }
        }

        // Create items
        if (addedIds.length > 0) {
            try {
                await Api.post(pathJoin(restEndpoint), addedIds.map(id => ({
                    ...editableData.find(d => d.id === id),
                    id: undefined,
                })));
            } catch (e) {
                console.error(e);
                Notify.error('Error Creating', `An error occurred while trying to create the ${resourceName} and the operation could not be finished`);
            }
        }

        // Modify
        for (let i = 0; i < changedIds.length; i++) {
            const id = changedIds[i];
            if (addedIds.includes(id)) continue;

            try {
                await Api.patch(pathJoin(restEndpoint, id), editableData.find(d => d.id === id))
            } catch (e) {
                console.error(e);
                Notify.error('Error Updating', `An error occurred while trying to update the ${resourceName} and the operation could not be finished`);
            }
        }

        setChangeIds([]);
        setAddedIds([]);
        setDeletedIds([]);

        refreshData();
        setDisablePaging(false);
        setIsEditing(false);
    }

    const processData = () => {
        return editableData.filter(item => !deletedIds.includes(item.id)).map(item => ({
            id: item.id,
            select: <Checkbox
                className={styles.Checkbox}
                checked={selected.includes(item.id)}
                onChange={e => updateSelected(item.id, e)}
            />,
            selected: selected.includes(item.id),
            ...Object.fromEntries(Object.entries(columns).map(([key, c]) => [
                key,
                editing() ? (
                    <input
                        className={styles.Input}
                        type='text'
                        value={item[key]}
                        onChange={e => handleFieldChange(item.id, key, e.target.value)}
                    />
                ) : (
                    c.formatFunction ? c.formatFunction(item) : item[key]
                ),
            ])),
            ...(
                editing() ? {
                    delete:
                        <Button
                            className={styles.DeleteButton}
                            onClick={() => handleDelete(item.id)}
                        >
                            Delete
                        </Button>
                } : {}
            )
        }));
    };

    const getColumns = () => (!editing()
    ?
        isSelectable() ?
        {
            select: {
                label: '',
                sortable: false,
                size: '82px',
                passOnClick: true
            },
            ...columns
        }
        :
        columns
    : {
        ...columns,
        ...(
            editing() ? {
                delete: { label: 'Actions', sortable: false, size: '100px' }
            } : {}
        )
    });

    const cancel = () => {
        setEditableData(rawData);
        setIsEditing(false);
    };

    const add = () => {
        const id = Math.ceil(Math.random() * 1e10);

        setEditableData(d => [{id}, ...d]);
        setAddedIds(ids => [...ids, id]);
    }

    return (<>
        {
            (isEditing && editMode) && (<div className={styles.Actions}>
                <Button className='primary' onClick={save}>
                    Save
                </Button>
                <Button className='primary' onClick={add}>
                    Add
                </Button>
                <Button onClick={cancel}>
                    Cancel
                </Button>
            </div>)
        }

        <Grid
            columns={getColumns()}
            data={loading ? loadingDataSet() : processData()}
            stickyHeader={stickyHeader}
            initialSort={sort}
            onSort={setSort}
        />
    </>
    );
};

/**
 * @typedef {number} QueryGridSelectModeEnum
 * @enum {QueryGridSelectModeEnum}
 */
export const QueryGridSelectMode = {
    MultiIfWritable: 0,
    Multi: 1,
    PickIfWritable: 2,
    Pick: 3,
    None: 4
}